import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Calendar, MapPin, ShareNetwork, Trophy } from '@phosphor-icons/react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// (Msg 746) Game Highlights — public listing + per-match detail pages
// rendering auto-generated Claude Sonnet 4.5 recaps stored in `match_recaps`.
// Designed for organic search traffic — JSON-LD NewsArticle schema, Open
// Graph meta, semantic <article> markup, and friendly slugs like
// `/blog/game-highlights/mexico-vs-south-africa-2026-06-11`.

const shareRecap = async (recap) => {
  const url = `${window.location.origin}/blog/game-highlights/${recap.slug}`;
  const title = `${recap.title} — World Soccer Party`;
  const text = recap.meta_description || recap.title;
  try {
    if (navigator.share) { await navigator.share({ title, text, url }); return; }
  } catch (e) { if (e && e.name === 'AbortError') return; }
  try {
    await navigator.clipboard.writeText(`${text} ${url}`);
    toast.success('Link copied — paste it anywhere!');
  } catch { toast.error('Could not share. Copy: ' + url); }
};

const fmtScore = (r) => `${r.home} ${r.home_score}-${r.away_score} ${r.away}`;

const RecapDetail = ({ slug }) => {
  const navigate = useNavigate();
  const [recap, setRecap] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/recaps/${slug}`);
        if (!alive) return;
        setRecap(data);
        // Load related
        const list = await axios.get(`${API}/api/recaps?limit=6`);
        if (!alive) return;
        setRelated((list.data.items || []).filter(r => r.slug !== slug).slice(0, 3));
      } catch (e) {
        setError(e?.response?.status === 404 ? 'Recap not found' : 'Could not load recap');
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen pt-32 pb-24 bg-gray-50 text-center" data-testid="recap-error">
        <p className="text-gray-700 text-lg mb-4">{error}</p>
        <button onClick={() => navigate('/blog/game-highlights')} className="text-[#FF6B35] font-bold underline">← Back to Game Highlights</button>
      </div>
    );
  }
  if (!recap) {
    return (
      <div className="min-h-screen pt-32 pb-24 bg-gray-50 text-center" data-testid="recap-loading">
        <p className="text-gray-500">Loading recap…</p>
      </div>
    );
  }

  // JSON-LD NewsArticle schema for Google rich results
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: recap.title,
    description: recap.meta_description,
    image: [recap.hero_image],
    datePublished: recap.created_at,
    dateModified: recap.updated_at || recap.created_at,
    author: { "@type": "Organization", name: "World Soccer Party" },
    publisher: {
      "@type": "Organization",
      name: "World Soccer Party",
      logo: { "@type": "ImageObject", url: `${window.location.origin}/wsp-logo.png` },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${window.location.origin}/blog/game-highlights/${recap.slug}`,
    },
    articleSection: "Game Highlights",
    keywords: (recap.tags || []).join(", "),
  };

  return (
    <div className="min-h-screen pt-32 pb-24 bg-gray-50" data-testid="recap-detail">
      <Helmet>
        <title>{`${recap.title} | World Soccer Party`}</title>
        <meta name="description" content={recap.meta_description} />
        <meta name="keywords" content={(recap.tags || []).join(', ')} />
        <link rel="canonical" href={`${window.location.origin}/blog/game-highlights/${recap.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={recap.title} />
        <meta property="og:description" content={recap.meta_description} />
        <meta property="og:image" content={recap.hero_image} />
        <meta property="og:url" content={`${window.location.origin}/blog/game-highlights/${recap.slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={recap.title} />
        <meta name="twitter:description" content={recap.meta_description} />
        <meta name="twitter:image" content={recap.hero_image} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <article className="max-w-3xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <Link to="/blog/game-highlights" className="inline-flex items-center gap-1 text-[#FF6B35] font-bold text-sm hover:underline" data-testid="back-to-game-highlights">
            <ArrowLeft size={16} weight="bold" /> Game Highlights
          </Link>
          <button
            type="button"
            onClick={() => shareRecap(recap)}
            className="inline-flex items-center gap-1.5 bg-white border-2 border-[#FF6B35] text-[#FF6B35] text-xs font-bold px-4 py-2 rounded-full hover:bg-[#FF6B35] hover:text-white transition-colors"
            data-testid="recap-share-btn"
          >
            <ShareNetwork size={14} weight="bold" /> Share
          </button>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="relative rounded-2xl overflow-hidden mb-8 shadow-lg" style={recap.team_colors?.primary ? { boxShadow: `0 0 0 4px ${recap.team_colors.primary}, 0 10px 40px rgba(0,0,0,0.15)` } : undefined}>
            <img src={recap.hero_image} alt={`${recap.home} vs ${recap.away}`} className="w-full h-72 sm:h-96 object-cover" />
            {/* (Msg 755) Team-colour gradient overlay — winning side's
                primary fades from bottom-left so the hero visually matches
                the article subject without distracting from the photo. */}
            <div
              className="absolute inset-0"
              style={recap.team_colors?.primary
                ? { background: `linear-gradient(135deg, ${recap.team_colors.primary}cc 0%, transparent 45%, rgba(0,0,0,0.85) 100%)` }
                : { background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)' }
              }
            />
            {recap.photo_credit && (
              <span
                className="absolute bottom-2 right-2 text-[10px] font-semibold text-white/85 bg-black/45 backdrop-blur-sm px-2 py-1 rounded"
                data-testid="recap-photo-credit"
              >
                {recap.photo_credit}
              </span>
            )}
            <div className="absolute bottom-5 left-5 right-5 text-white">
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#FFD700] mb-2 flex items-center gap-1.5">
                <Trophy size={14} weight="fill" /> Game Highlights · World Soccer 2026
              </p>
              <p className="font-['Bebas_Neue'] text-4xl sm:text-5xl uppercase leading-none">
                {fmtScore(recap)}
              </p>
              <p className="mt-2 text-sm opacity-90 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1"><MapPin size={12} weight="bold" /> {recap.stadium}, {recap.city}</span>
                <span className="inline-flex items-center gap-1"><Calendar size={12} weight="bold" /> {recap.match_date}</span>
                {recap.group && <span>Group {recap.group}</span>}
              </p>
            </div>
          </div>

          <h1 className="font-['Bebas_Neue'] text-3xl sm:text-4xl uppercase text-gray-900 mb-4 leading-tight" data-testid="recap-title">
            {recap.title}
          </h1>

          {recap.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {recap.tags.map(t => (
                <span
                  key={t}
                  className="text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full"
                  style={recap.team_colors?.primary
                    ? { backgroundColor: `${recap.team_colors.primary}1a`, color: recap.team_colors.primary }
                    : { backgroundColor: 'rgba(255,107,53,0.1)', color: '#FF6B35' }
                  }
                >{t}</span>
              ))}
            </div>
          )}

          <div
            className="prose prose-lg max-w-none text-gray-800 leading-relaxed mb-12 [&_h2]:font-['Bebas_Neue'] [&_h2]:text-3xl [&_h2]:uppercase [&_h2]:text-gray-900 [&_h2]:mt-8 [&_h2]:mb-3 [&_p]:mb-4"
            data-testid="recap-body"
            // The recap body is sanitised server-side (script/style tags stripped).
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: recap.body_html }}
          />

          {/* Goals scorecard */}
          {recap.goals?.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow mb-12" data-testid="recap-goals">
              <h3 className="font-['Bebas_Neue'] text-2xl uppercase text-gray-900 mb-4">Goals</h3>
              <ul className="space-y-2">
                {recap.goals.map((g, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-800">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#FF6B35] text-white font-black text-sm">{g.minute}'</span>
                    <span className="font-semibold">{g.scorer}</span>
                    <span className="text-gray-500 text-sm">({g.team === 'home' ? recap.home : recap.away})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {related.length > 0 && (
            <div className="mt-12 border-t pt-8" data-testid="recap-related">
              <h3 className="font-['Bebas_Neue'] text-2xl uppercase text-gray-900 mb-4">More Game Highlights</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {related.map(r => (
                  <Link key={r.slug} to={`/blog/game-highlights/${r.slug}`} className="block bg-white rounded-xl overflow-hidden shadow hover:shadow-lg transition-all hover:-translate-y-0.5" data-testid={`related-${r.slug}`}>
                    <img src={r.hero_image} alt={r.title} className="w-full h-32 object-cover" />
                    <div className="p-3">
                      <p className="font-bold text-sm text-gray-900 line-clamp-2">{r.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{fmtScore(r)} · {r.city}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </article>
    </div>
  );
};

const RecapList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchItems = async () => {
      try {
        const { data } = await axios.get(`${API}/api/recaps?limit=50`);
        if (!alive) return;
        setItems(data.items || []);
      } catch (e) {
        // network errors — leave empty, user sees the "no recaps yet" state
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchItems();
    // (Msg 750) Real-time: poll every 45s so a recap that just auto-publishes
    // shows up without the visitor refreshing.
    let intervalId = setInterval(fetchItems, 45_000);
    const visHandler = () => {
      if (document.hidden) { if (intervalId) { clearInterval(intervalId); intervalId = null; } }
      else { fetchItems(); if (!intervalId) intervalId = setInterval(fetchItems, 45_000); }
    };
    document.addEventListener('visibilitychange', visHandler);
    return () => { alive = false; if (intervalId) clearInterval(intervalId); document.removeEventListener('visibilitychange', visHandler); };
  }, []);

  return (
    <div className="min-h-screen pt-32 pb-24 bg-gray-50" data-testid="game-highlights-list">
      <Helmet>
        <title>Game Highlights · World Soccer 2026 Recaps | World Soccer Party</title>
        <meta name="description" content="Match-by-match recaps from World Soccer 2026 across Canada, USA, and Mexico. Goals, atmosphere, group standings, and where to watch the next game." />
        <link rel="canonical" href={`${window.location.origin}/blog/game-highlights`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Game Highlights · World Soccer 2026 Recaps" />
        <meta property="og:description" content="Match-by-match recaps. Goals. Atmosphere. Group standings. Updated minutes after the final whistle." />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4">
        <Link to="/blog" className="inline-flex items-center gap-1 text-[#FF6B35] font-bold text-sm hover:underline mb-6" data-testid="back-to-blog-from-highlights">
          <ArrowLeft size={16} weight="bold" /> Back to Blog
        </Link>

        <div className="text-center mb-12">
          <p className="text-[11px] font-black uppercase tracking-[0.3em] text-[#FFD700] mb-3 flex items-center justify-center gap-1.5">
            <Trophy size={14} weight="fill" className="text-[#FF6B35]" /> Auto-Updated · World Soccer 2026
          </p>
          <h1 className="font-['Bebas_Neue'] text-5xl sm:text-6xl uppercase text-gray-900 leading-none">Game Highlights</h1>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            Match-by-match recaps from across Canada, USA, and Mexico. Goals, atmosphere, group standings — published minutes after the final whistle.
          </p>
        </div>

        {loading && (
          <div className="text-center text-gray-500" data-testid="highlights-loading">Loading…</div>
        )}

        {!loading && items.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-300" data-testid="highlights-empty">
            <Trophy size={40} weight="bold" className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-700 font-bold text-lg mb-1">First recap drops after kickoff</p>
            <p className="text-gray-500 text-sm">Game Highlights publish automatically about 15 min after each match's final whistle.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((r, index) => (
            <motion.div key={r.slug} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.05 }} className="relative">
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); shareRecap(r); }}
                className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-white/95 backdrop-blur border-2 border-[#FF6B35] text-[#FF6B35] text-xs font-bold px-2.5 py-1 rounded-full hover:bg-[#FF6B35] hover:text-white transition-colors shadow"
                data-testid={`highlight-share-${r.slug}`}
                aria-label={`Share ${r.title}`}
              ><ShareNetwork size={12} weight="bold" /> Share</button>

              <Link to={`/blog/game-highlights/${r.slug}`} className="block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100 group" data-testid={`highlight-card-${r.slug}`}>
                <div className="relative overflow-hidden">
                  <img src={r.hero_image} alt={`${r.home} vs ${r.away}`} className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#FFD700] mb-1">Final · Group {r.group}</p>
                    <p className="font-['Bebas_Neue'] text-3xl uppercase leading-none">{fmtScore(r)}</p>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1"><Calendar size={12} weight="bold" />{r.match_date}</span>
                    <span className="inline-flex items-center gap-1"><MapPin size={12} weight="bold" />{r.city}</span>
                  </div>
                  <h2 className="font-['Bebas_Neue'] text-xl uppercase text-gray-900 mb-2 line-clamp-2">{r.title}</h2>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">{r.meta_description}</p>
                  <span className="inline-flex items-center gap-1 text-[#FF6B35] font-bold text-sm group-hover:gap-2 transition-all">Read Recap <ArrowRight size={16} weight="bold" /></span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

const GameHighlightsPage = () => {
  const { slug } = useParams();
  return slug ? <RecapDetail slug={slug} /> : <RecapList />;
};

export default GameHighlightsPage;
